import React, { useState } from 'react';
import {
  Modal,
  Stepper,
  TextInput,
  Textarea,
  SegmentedControl,
  MultiSelect,
  Select,
  NumberInput,
  Switch,
  Button,
  Group,
  Stack,
  Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useTranslation } from 'react-i18next';
import {
  useCreateReview360Cycle,
  useQuestionTemplates,
  useReview360People,
} from '../../hooks/useApi';
import { useAnalytics, AnalyticsEvents } from '../../analytics';
import { showToast } from '../../utils/toast';
import { ReviewCycle, ReviewCycleType } from '../../types';

interface CycleCreateModalProps {
  opened: boolean;
  onClose: () => void;
}

export const CycleCreateModal: React.FC<CycleCreateModalProps> = ({ opened, onClose }) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const createCycle = useCreateReview360Cycle();
  // Оцениваемые — те, по кому сервер разрешает запустить цикл (охват review360.run).
  const { data: users = [] } = useReview360People();
  const { data: templates = [] } = useQuestionTemplates();

  // Step
  const [activeStep, setActiveStep] = useState(0);

  // Step 1 fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<ReviewCycleType>('360');
  const [subjects, setSubjects] = useState<string[]>([]);
  const [questionTemplateSourceId, setQuestionTemplateSourceId] = useState<string | null>(null);
  const [nominationDeadline, setNominationDeadline] = useState<Date | null>(null);
  const [responseDeadline, setResponseDeadline] = useState<Date | null>(null);

  // Step 2 fields
  const [isManagerAnonymous, setIsManagerAnonymous] = useState(false);
  const [peerMinThreshold, setPeerMinThreshold] = useState<number>(3);
  const [subordinateMinThreshold, setSubordinateMinThreshold] = useState<number>(3);
  const [isCollapseSmallGroups, setIsCollapseSmallGroups] = useState(true);

  const resetForm = () => {
    setActiveStep(0);
    setTitle('');
    setDescription('');
    setType('360');
    setSubjects([]);
    setQuestionTemplateSourceId(null);
    setNominationDeadline(null);
    setResponseDeadline(null);
    setIsManagerAnonymous(false);
    setPeerMinThreshold(3);
    setSubordinateMinThreshold(3);
    setIsCollapseSmallGroups(true);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isStep1Valid = title.trim().length > 0 && subjects.length > 0 && !!questionTemplateSourceId;

  const handleSubmit = async () => {
    try {
      await createCycle.mutateAsync({
        title,
        description,
        type,
        subjects: subjects as unknown as ReviewCycle['subjects'],
        questionTemplateSourceId: questionTemplateSourceId ?? undefined,
        deadlines: {
          nomination: nominationDeadline ? nominationDeadline.toISOString() : '',
          response: responseDeadline ? responseDeadline.toISOString() : '',
        },
        anonymitySettings: {
          managerAnonymous: isManagerAnonymous,
          peerMinThreshold,
          subordinateMinThreshold,
          collapseSmallGroups: isCollapseSmallGroups,
        },
      });

      track(AnalyticsEvents.REVIEW_360_CYCLE_CREATED, { type, subjectCount: subjects.length });

      showToast({
        title: t('common.success'),
        message: t('review360.createCycle'),
        color: 'teal',
      });

      handleClose();
    } catch {
      showToast({
        title: t('common.error'),
        message: String(t('common.error')),
        color: 'red',
      });
    }
  };

  const usersData = users.map((u) => ({ value: u._id, label: u.name }));
  const templatesData = templates.map((tpl) => ({ value: tpl._id, label: tpl.name }));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('review360.createCycle')}
      size="lg"
      centered
    >
      <Stepper active={activeStep} onStepClick={setActiveStep} size="sm" mb="lg">
        <Stepper.Step label={t('review360.step1')}>
          <Stack gap="md">
            <TextInput
              label={t('review360.cycleName')}
              required
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
            />
            <Textarea
              label={t('review360.cycleDescription')}
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
            />
            <div>
              <Text size="sm" fw={500} mb={4}>
                {t('review360.cycleType')}
              </Text>
              <SegmentedControl
                value={type}
                onChange={(v) => setType(v as ReviewCycleType)}
                data={[
                  { label: t('review360.type360'), value: '360' },
                  { label: t('review360.type180'), value: '180' },
                ]}
                fullWidth
              />
            </div>
            <MultiSelect
              label={t('review360.subjects')}
              data={usersData}
              value={subjects}
              onChange={setSubjects}
              searchable
              nothingFoundMessage={t('review360.noResults')}
            />
            <Select
              label={t('review360.template')}
              data={templatesData}
              value={questionTemplateSourceId}
              onChange={setQuestionTemplateSourceId}
              clearable
              nothingFoundMessage={t('review360.noTemplates')}
            />
            <DatePickerInput
              label={t('review360.nominationDeadline')}
              value={nominationDeadline}
              onChange={(value) => setNominationDeadline(value as Date | null)}
            />
            <DatePickerInput
              label={t('review360.responseDeadline')}
              value={responseDeadline}
              onChange={(value) => setResponseDeadline(value as Date | null)}
            />
          </Stack>
          <Group justify="flex-end" mt="xl">
            <Button onClick={() => setActiveStep(1)} disabled={!isStep1Valid}>
              {t('common.next')}
            </Button>
          </Group>
        </Stepper.Step>

        <Stepper.Step label={t('review360.step2')}>
          <Stack gap="md">
            <Switch
              label={t('review360.anonymity.managerAnonymous')}
              checked={isManagerAnonymous}
              onChange={(e) => setIsManagerAnonymous(e.currentTarget.checked)}
            />
            <NumberInput
              label={t('review360.anonymity.peerThreshold')}
              value={peerMinThreshold}
              onChange={(v) => setPeerMinThreshold(typeof v === 'number' ? v : 3)}
              min={3}
              max={10}
            />
            <NumberInput
              label={t('review360.anonymity.subordinateThreshold')}
              value={subordinateMinThreshold}
              onChange={(v) => setSubordinateMinThreshold(typeof v === 'number' ? v : 3)}
              min={3}
              max={10}
            />
            <Switch
              label={t('review360.anonymity.collapseSmallGroups')}
              checked={isCollapseSmallGroups}
              onChange={(e) => setIsCollapseSmallGroups(e.currentTarget.checked)}
            />
            <Text c="dimmed" size="xs">
              {t('review360.anonymity.collapseHelp')}
            </Text>
          </Stack>
          <Group justify="space-between" mt="xl">
            <Button variant="default" onClick={() => setActiveStep(0)}>
              {t('common.back')}
            </Button>
            <Button onClick={handleSubmit} loading={createCycle.isPending}>
              {t('common.create')}
            </Button>
          </Group>
        </Stepper.Step>
      </Stepper>
    </Modal>
  );
};
